/**
 * Typed wrappers for the admin API (cmd/admin on :8081).
 * Mirrors the structure of endpoints.ts but for admin routes.
 */

import { adminFetch } from "./admin-api";

// --- wire types -----------------------------------------------------------

export interface AdminLoginResponse {
  token: string;
  expires_at: string;
}

export interface AdminMe {
  id: string;
  email: string;
}

export type EligibilityStatus = "eligible" | "out_of_scope" | "manual_review";

export interface AdminCaseSummary {
  id: string;
  case_type: string;
  status: string;
  eligibility_status?: EligibilityStatus | null;
  paid_at?: string | null;
  created_at: string;
  updated_at: string;
  owner_id: string;
  owner_email: string;
  owner_name?: string | null;
}

export interface AdminCaseEvent {
  id: string;
  event_type: string;
  actor_type: string;
  actor_user_id?: string | null;
  event_data?: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminCaseDetail extends AdminCaseSummary {
  applicant_person_id?: string | null;
  beneficiary_person_id?: string | null;
  gating_answers?: Record<string, unknown>;
  fee_total_cents?: number | null;
  finalized_at?: string | null;
  submitted_at?: string | null;
  events: AdminCaseEvent[];
}

export interface AdminCaseListResponse {
  items: AdminCaseSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminReviewRequest {
  eligibility_status: EligibilityStatus;
  note?: string;
}

export interface AdminReviewResponse {
  case_id: string;
  eligibility_status: EligibilityStatus;
}

export type CaseStatusAction = "approve" | "block";

export interface AdminStatusRequest {
  action: CaseStatusAction;
  notes?: string;
}

export interface AdminStatusResponse {
  case_id: string;
  status: string;
}

// --- Document types -------------------------------------------------------

export interface AdminDocumentSummary {
  id: string;
  case_id: string;
  uploader_email?: string | null;
  file_name_original?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  document_type?: string | null;
  processing_state: string;
  review_status?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminDocumentDetail extends AdminDocumentSummary {
  file_key: string;
  sha256_hex?: string;
  evidences_entity_type?: string | null;
  evidences_entity_id?: string | null;
  download_url: string;
  download_url_expires: string;
}

export interface AdminDocumentListResponse {
  items: AdminDocumentSummary[];
  total: number;
  limit: number;
  offset: number;
}

export type DocReviewAction = "pass" | "block";

export interface AdminDocReviewRequest {
  action: DocReviewAction;
  notes?: string;
}

// --- endpoint helpers -----------------------------------------------------

export const adminAuth = {
  login: (email: string, password: string) =>
    adminFetch<AdminLoginResponse>("/admin/auth/login", {
      method: "POST",
      json: { email, password },
    }),

  me: () => adminFetch<AdminMe>("/admin/me"),
};

export const adminCases = {
  list: (params?: {
    status?: string;
    eligibility?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.eligibility) qs.set("eligibility", params.eligibility);
    if (params?.limit != null) qs.set("limit", String(params.limit));
    if (params?.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return adminFetch<AdminCaseListResponse>(`/admin/cases${query ? `?${query}` : ""}`);
  },

  get: (id: string) => adminFetch<AdminCaseDetail>(`/admin/cases/${id}`),

  review: (id: string, body: AdminReviewRequest) =>
    adminFetch<AdminReviewResponse>(`/admin/cases/${id}/review`, {
      method: "POST",
      json: body,
    }),

  /** POST /admin/cases/{id}/status — approve (→ submitted) or block (→ closed) */
  status: (id: string, body: AdminStatusRequest) =>
    adminFetch<AdminStatusResponse>(`/admin/cases/${id}/status`, {
      method: "POST",
      json: body,
    }),
};

// --- Documents endpoints --------------------------------------------------

export const adminDocuments = {
  list: (params?: {
    review_status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.review_status) qs.set("review_status", params.review_status);
    if (params?.limit != null) qs.set("limit", String(params.limit));
    if (params?.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return adminFetch<AdminDocumentListResponse>(`/admin/documents${query ? `?${query}` : ""}`);
  },

  get: (id: string) => adminFetch<AdminDocumentDetail>(`/admin/documents/${id}`),

  review: (id: string, body: AdminDocReviewRequest) =>
    adminFetch<{ id: string; review_status: string }>(`/admin/documents/${id}/review`, {
      method: "POST",
      json: body,
    }),
};
